import {
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { type Dispatch, type SetStateAction, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  type BasicCredentialFormData,
  BasicLoginForm,
  validateBasicFormData,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import { TOTPSecretInput } from '@/sections/asset/components/tabs/credentials/TOTPSecretInput';
import {
  classifyField,
  pickFields,
  selectFormShape,
} from '@/sections/insights/aiAssistant/utils/interactionFields';
import type { Interaction } from '@/types';

interface InteractionPromptBodyProps {
  interaction: Interaction;
  /**
   * Answers the interaction in its OWN conversation. The body always passes
   * `interaction.conversationId` so a subagent child's prompt is answered
   * cross-conversation (see useInteractions.submitCredential).
   */
  onSubmit: (
    conversationId: string,
    requestId: string,
    inputs: Record<string, string>
  ) => Promise<void>;
}

interface DynamicField {
  name: string;
  value: string;
  /**
   * True for a field the AGENT requested (pre-seeded from `interaction.fields`),
   * false for one the operator added by hand. Seeded fields have a read-only
   * name (renaming would evade the completeness check below AND break the
   * agent's `{{name}}` placeholder) and, unless they name a TOTP field, are
   * REQUIRED — see `isRequiredDynField`.
   */
  seeded: boolean;
}

// TOTP/MFA is always optional: not every target enrolls a second factor, so an
// agent-requested TOTP field must never block submitting the rest (mirrors the
// `basicTotp` shape, which also never requires its TOTP input).
const isTotpField = (name: string) => {
  const kind = classifyField(name);
  return kind === 'totpSecret' || kind === 'totpCode';
};

// A dynamic row is REQUIRED iff the agent asked for it and it is not a TOTP
// field. Removing a seeded row is the deliberate escape hatch (the agent guessed
// a field the target doesn't have); leaving a still-present seeded field blank
// is the accident this guard catches. Operator-added rows are always optional.
const isRequiredDynField = (f: DynamicField) =>
  f.seeded && !isTotpField(f.name);

/**
 * Presentation-agnostic credential-request form body (G4: presentational only).
 * Renders the correct control set for the agent's requested field-shape —
 * reusing the shared credentialForms building blocks for the common cases:
 *
 *   - `basic` / `basicTotp` → shared {@link BasicLoginForm} (+ {@link TOTPSecretInput}
 *     for a base32 seed, a masked input for a live code)
 *   - `singleToken`        → one masked input (D5)
 *   - `totpCode`           → one numeric live-code input
 *   - `dynamic`            → editable name/value card for arbitrary shapes
 *
 * On submit the pure {@link pickFields} adapter builds the flat KV the transient
 * ephemeral-broker submit expects, emitting ONLY the agent's requested names
 * (G7) — the cosmetic `label` is never sent. Mount inside any container (the
 * drawer surface); this component owns no placement.
 */
export function InteractionPromptBody({
  interaction,
  onSubmit,
}: InteractionPromptBodyProps) {
  const { shape, fields } = selectFormShape(interaction.fields);

  const usernameName = fields.find(f => classifyField(f) === 'username');
  const passwordName = fields.find(f => classifyField(f) === 'password');
  const totpFields = fields.filter(f => {
    const kind = classifyField(f);
    return kind === 'totpSecret' || kind === 'totpCode';
  });

  // Structured-shape state: one flat map keyed EXCLUSIVELY by the agent's
  // requested field names. The BasicLoginForm's cosmetic "Label" lives in its
  // OWN state (basicLabel) and is never written here, so `values` can never
  // contain a `label` key — pickFields therefore cannot emit the cosmetic label
  // as a credential even if an agent requests a field literally named `label`.
  const [values, setValues] = useState<Record<string, string>>({});
  const [basicLabel, setBasicLabel] = useState('');
  // Dynamic-shape state: an editable name/value list for arbitrary shapes.
  const [dynFields, setDynFields] = useState<DynamicField[]>(
    fields.map(name => ({ name, value: '', seeded: true }))
  );
  // Per-row "this required field is blank" flags, keyed by row INDEX (not name —
  // an operator could add two blank-named rows, and names are non-unique until
  // filled). Populated on a failed submit, cleared as the operator types.
  const [dynErrors, setDynErrors] = useState<Record<number, boolean>>({});
  // Focus the first offending value input on a failed submit (a11y: don't just
  // paint an error the operator has to hunt for).
  const dynValueRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setValue = (name: string, value: string) =>
    setValues(prev => ({ ...prev, [name]: value }));

  // Adapter so BasicLoginForm's ({label,username,password}, setState) contract
  // drives our flat `values` map keyed by the agent's ACTUAL requested names.
  const basicFormData: BasicCredentialFormData = {
    label: basicLabel,
    username: (usernameName && values[usernameName]) || '',
    password: (passwordName && values[passwordName]) || '',
  };
  const onBasicChange: Dispatch<
    SetStateAction<BasicCredentialFormData>
  > = updater => {
    const current: BasicCredentialFormData = {
      label: basicLabel,
      username: (usernameName && values[usernameName]) || '',
      password: (passwordName && values[passwordName]) || '',
    };
    const next = typeof updater === 'function' ? updater(current) : updater;
    // The cosmetic label goes to its own state — NEVER into `values`.
    setBasicLabel(next.label);
    setValues(prev => {
      const merged: Record<string, string> = { ...prev };
      if (usernameName) merged[usernameName] = next.username;
      if (passwordName) merged[passwordName] = next.password;
      return merged;
    });
  };

  const setDynField = (idx: number, patch: Partial<DynamicField>) => {
    setDynFields(prev =>
      prev.map((f, i) => (i === idx ? { ...f, ...patch } : f))
    );
    // Typing into a flagged row clears its error immediately.
    if (patch.value !== undefined && dynErrors[idx]) {
      setDynErrors(prev => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
    }
  };
  const addDynField = () =>
    setDynFields(prev => [...prev, { name: '', value: '', seeded: false }]);
  const removeDynField = (idx: number) => {
    setDynFields(prev => prev.filter((_, i) => i !== idx));
    // Row indices shift on removal, so a stale index→error map would mis-paint
    // the wrong row. Errors are only ever set at submit time, so clearing here
    // is safe and keeps the map aligned with the live rows.
    setDynErrors({});
  };

  const buildInputs = (): Record<string, string> | null => {
    if (shape === 'dynamic') {
      // Completeness: every REQUIRED row still present must carry a non-blank
      // value (trimmed — whitespace is not a value). A partially-filled request
      // would send the agent a subset, breaking its remaining `{{name}}`
      // substitutions; the escape hatch for a field the target lacks is to
      // REMOVE the row, not to leave it blank.
      const errors: Record<number, boolean> = {};
      dynFields.forEach((f, idx) => {
        if (isRequiredDynField(f) && f.value.trim() === '') errors[idx] = true;
      });
      if (Object.keys(errors).length > 0) {
        setDynErrors(errors);
        const firstInvalid = Object.keys(errors)
          .map(Number)
          .sort((a, b) => a - b)[0];
        dynValueRefs.current[firstInvalid]?.focus();
        return null;
      }
      const inputs: Record<string, string> = {};
      for (const f of dynFields) {
        const name = f.name.trim();
        if (name && f.value.trim() !== '') inputs[name] = f.value;
      }
      return Object.keys(inputs).length > 0 ? inputs : null;
    }

    if (shape === 'basic' || shape === 'basicTotp') {
      const error = validateBasicFormData(basicFormData);
      if (error) {
        toast.error(error);
        return null;
      }
    }

    const inputs = pickFields(values, fields);
    return Object.keys(inputs).length > 0 ? inputs : null;
  };

  const handleSubmit = async () => {
    const inputs = buildInputs();
    if (!inputs) {
      if (shape === 'dynamic') {
        // A required-field failure already painted inline errors + focused the
        // first one; only the genuinely-empty form needs the fallback toast.
        const hasRequiredError = dynFields.some(
          f => isRequiredDynField(f) && f.value.trim() === ''
        );
        if (!hasRequiredError) {
          toast.error('Enter at least one credential value');
        }
      } else if (shape !== 'basic' && shape !== 'basicTotp') {
        toast.error('Enter at least one credential value');
      }
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(interaction.conversationId, interaction.requestId, inputs);
    } catch (err) {
      // Never log the raw error: an AxiosError's `config.data` is the serialized
      // POST body carrying the plaintext credentials, and LogRocket console
      // capture is NOT governed by the network requestSanitizer.
      console.error(
        'Failed to submit credentials:',
        err instanceof Error ? err.message : 'submit failed'
      );
      toast.error('Failed to send credentials to the agent');
      setSubmitting(false);
    }
  };

  return (
    <form
      aria-label="Credential request form"
      onSubmit={e => {
        e.preventDefault();
        if (!submitting) void handleSubmit();
      }}
      className="flex min-h-0 flex-1 flex-col"
    >
      {/*
       * data-private: LogRocket omits this element and ALL its descendants'
       * text/inputs from DOM session replay. This covers the reveal-toggled
       * inputs (type flips to `text`) AND the non-password username/totp_code
       * inputs, so no typed credential value is ever captured in replay.
       */}
      <div data-private className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        <div className="border-chariot-brand-primary/30 bg-chariot-brand-primary/5 text-chariot-text-secondary flex items-start gap-2 rounded-lg border px-3 py-2 text-xs leading-relaxed">
          <ShieldCheckIcon className="text-chariot-brand-primary mt-0.5 size-4 shrink-0" />
          <span>
            Sent <strong>once</strong> straight to the access broker for this
            session — <strong>never stored in this chat</strong> and never seen
            by the AI model.
          </span>
        </div>

        {interaction.request && (
          <p className="text-chariot-text-secondary text-sm leading-relaxed">
            {interaction.request}
          </p>
        )}

        {(shape === 'basic' || shape === 'basicTotp') && (
          <div className="space-y-4">
            <BasicLoginForm formData={basicFormData} onChange={onBasicChange} />
            {shape === 'basicTotp' &&
              totpFields.map(name =>
                classifyField(name) === 'totpSecret' ? (
                  <TOTPSecretInput
                    key={name}
                    value={values[name] ?? ''}
                    onChange={secret => setValue(name, secret)}
                  />
                ) : (
                  <label key={name} className="block">
                    <span className="text-chariot-text-primary mb-2 block text-sm font-medium">
                      {name}
                    </span>
                    <Input
                      inputMode="numeric"
                      autoComplete="off"
                      value={values[name] ?? ''}
                      placeholder="6-digit code"
                      disabled={submitting}
                      onChange={e => setValue(name, e.target.value)}
                    />
                  </label>
                )
              )}
          </div>
        )}

        {shape === 'singleToken' && (
          <label className="block">
            <span className="text-chariot-text-primary mb-2 flex items-center justify-between text-sm font-medium">
              {fields[0]}
              <button
                type="button"
                onClick={() => setRevealed(v => !v)}
                className="text-chariot-text-secondary hover:text-chariot-text-primary"
                aria-label={revealed ? 'Hide value' : 'Show value'}
              >
                {revealed ? (
                  <EyeIcon className="size-4" />
                ) : (
                  <EyeSlashIcon className="size-4" />
                )}
              </button>
            </span>
            <Input
              type={revealed ? 'text' : 'password'}
              autoComplete="off"
              value={values[fields[0]] ?? ''}
              placeholder="value"
              disabled={submitting}
              onChange={e => setValue(fields[0], e.target.value)}
            />
          </label>
        )}

        {shape === 'totpCode' && (
          <label className="block">
            <span className="text-chariot-text-primary mb-2 block text-sm font-medium">
              {fields[0]}
            </span>
            <Input
              inputMode="numeric"
              autoComplete="off"
              value={values[fields[0]] ?? ''}
              placeholder="6-digit code"
              disabled={submitting}
              onChange={e => setValue(fields[0], e.target.value)}
            />
          </label>
        )}

        {shape === 'dynamic' && (
          <div className="space-y-2">
            <div className="flex items-center justify-end">
              {dynFields.some(f => f.value !== '') && (
                <button
                  type="button"
                  onClick={() => setRevealed(v => !v)}
                  className="text-chariot-text-secondary hover:text-chariot-text-primary"
                  aria-label={revealed ? 'Hide values' : 'Show values'}
                >
                  {revealed ? (
                    <EyeIcon className="size-4" />
                  ) : (
                    <EyeSlashIcon className="size-4" />
                  )}
                </button>
              )}
            </div>
            {dynFields.map((f, idx) => {
              const required = isRequiredDynField(f);
              const hasError = !!dynErrors[idx];
              const errorId = `interaction-dyn-error-${idx}`;
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center gap-2">
                    {f.seeded ? (
                      // Agent-requested name is READ-ONLY: renaming it would
                      // evade the completeness guard and break the agent's
                      // `{{name}}` placeholder substitution.
                      <span
                        title={f.name}
                        className="text-chariot-text-secondary w-32 shrink-0 truncate font-mono text-xs"
                      >
                        {f.name}
                        {required && (
                          <span className="text-red-500" aria-hidden="true">
                            {' '}
                            *
                          </span>
                        )}
                      </span>
                    ) : (
                      <Input
                        value={f.name}
                        placeholder="field"
                        disabled={submitting}
                        onChange={e =>
                          setDynField(idx, { name: e.target.value })
                        }
                        className="text-chariot-text-secondary w-32 shrink-0 font-mono text-xs"
                        aria-label={`Field ${idx + 1} name`}
                      />
                    )}
                    <Input
                      ref={el => {
                        dynValueRefs.current[idx] = el;
                      }}
                      type={revealed ? 'text' : 'password'}
                      value={f.value}
                      placeholder="value"
                      autoComplete="off"
                      disabled={submitting}
                      onChange={e =>
                        setDynField(idx, { value: e.target.value })
                      }
                      className="flex-1"
                      aria-label={f.seeded ? f.name : `Field ${idx + 1} value`}
                      aria-required={required || undefined}
                      aria-invalid={hasError || undefined}
                      aria-describedby={hasError ? errorId : undefined}
                    />
                    <button
                      type="button"
                      aria-label="Remove field"
                      disabled={submitting || dynFields.length === 1}
                      onClick={() => removeDynField(idx)}
                      className={cn(
                        'text-chariot-text-secondary hover:text-chariot-text-primary shrink-0 transition-colors',
                        dynFields.length === 1 && 'invisible'
                      )}
                    >
                      <XMarkIcon className="size-4" />
                    </button>
                  </div>
                  {hasError && (
                    <p id={errorId} className="pl-2 text-xs text-red-500">
                      Required — enter a value or remove this field.
                    </p>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              disabled={submitting}
              onClick={addDynField}
              className="text-chariot-brand-primary hover:text-chariot-brand-primary/80 flex items-center gap-1 text-sm font-medium transition-colors disabled:opacity-50"
            >
              <PlusIcon className="size-4" /> Add field
            </button>
          </div>
        )}
      </div>

      <div className="border-chariot-stroke flex items-center justify-between gap-3 border-t px-4 py-3">
        <span className="text-chariot-text-secondary flex items-center gap-1.5 text-xs">
          <ShieldCheckIcon className="size-4" /> Used once · not stored in this
          chat
        </span>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send securely'}
        </Button>
      </div>
    </form>
  );
}

export default InteractionPromptBody;
